import { useEffect, useMemo, useRef, useState } from 'react'
import { renderReport } from '../../lib/meshRenderer'
import {
  buildLibrary, libraryIds, draftPages,
} from '../../lib/caseReport/draft.js'
import { offeredPages } from '../../lib/caseReport/pageTypes.js'
import {
  FORMATS, formatFor, buildable, chromeFor, reportFileName, suggestFormat, COMMON_FORMATS,
  geometryFor,
} from '../../lib/caseReport/formats.js'
import {
  renderEntries, dataUrlToBytes, exportStem, toTransparentOverlay, logoBoxOf,
} from '../../lib/caseReport/pngExport.js'
import { omittedPages } from '../../lib/caseReport/formatPages.js'
import { usePageBasket } from '../../lib/caseReport/usePageBasket.js'
import { setPageInputs } from '../../lib/caseReport/basket.js'
import { ROLE_LABELS, STAGE_LABELS } from '../../lib/caseReport/classify.js'
import { sha256Hex, speLibrary, hashCandidates, matchGeometry, phiKeyWarning } from '../../lib/caseReport/speGeometry.js'
import { hasCephAnalysis, looseSheetMeta } from '../../lib/caseReport/schema.js'
import { makeResolveImage } from '../../lib/caseReport/renderScenes.js'
import { CHASSIS } from '../../lib/caseReport/template.js'
import { bgrDirectRemove } from '../../utils/bgr.js'
import { renderWithCache } from '../../lib/caseReport/renderCache.js'
import { buildCaseReport, buildBlankPage } from '../../lib/caseReport/caseReportDoc.js'
import { applyEdits } from '../../lib/caseReport/edits.js'
import { useCaseEdits } from '../../lib/caseReport/useCaseEdits.js'
import { saveDraftInputs, loadDraftInputs } from '../../lib/caseReport/editStore.js'
import ReportLookPanel, { useReportLook } from '../../components/ReportLookPanel.jsx'
import { BONE } from '../../lib/caseReport/palette.js'
import { buildStamp } from '../../lib/caseReport/buildStamp.js'
import {
  analyzeVolumeMeshes, ORBITAL_VOLUME_ROLES,
} from '../../lib/caseReport/meshAnalyze.js'
import { ORBITAL_CT_ROW_KEYS, isOldScan, studyAgeDays } from '../../lib/caseReport/orbitalRecipes.js'
import { CT_FIELDS } from '../../lib/sseReport.js'
import {
  mergeCtInfo, ctFetchStatus, ctFetchMessage, waiverSuggested, ctInfoFor,
} from '../../lib/caseReport/ctAutofill.js'
import { getReportMeta } from '../../services/api'
import PageEditor from './PageEditor.jsx'
import PartTypes from './PartTypes.jsx'
import PageList from './PageList.jsx'

/**
 * Case Report Builder — a case folder in, a branded case report out.
 *
 * This is deliberately NOT the existing "Case Report" tool, which composites
 * six background-removed PSDs into fixed slots on a pre-made template. That one
 * stays; the team uses it. This one starts from the meshes.
 *
 * The screen is ordered the way the work is: choose the case, LOOK at what was
 * detected, resolve anything the classifier could not decide, then draft.
 *
 * The two panels that matter are "Detected" and "Notes". The engine will not
 * choose between two meshes that claim the same role, and it reports every page
 * it did not build and every page it built without a part — because the failure
 * mode this whole tool exists to avoid is a report that LOOKS finished and is
 * quietly missing something. Those panels are that contract made visible.
 */

const card = 'bg-white rounded-xl border border-gray-200 p-4'
const field = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm placeholder-gray-400'
const btn = 'px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed'

const MESH_RE = /\.(stl|obj)$/i

/**
 * The page list as the draft should see it. An entry's `image` names an asset
 * held for this session; after a reload the name survives and the bytes do not,
 * so the page drafts as "not built — give this page its colour-map image"
 * instead of an empty box with a missing-image warning.
 */
function withLiveAssets(entries, assets) {
  return entries.map((e) => (
    e.inputs?.image && !assets.has(e.inputs.image)
      ? { ...e, inputs: { ...e.inputs, image: null } }
      : e))
}

/** A PNG blob with transparency, laid on white, as a data URL. */
function imageOnWhite(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const im = new Image()
    im.onload = () => {
      const c = document.createElement('canvas')
      c.width = im.naturalWidth; c.height = im.naturalHeight
      const ctx = c.getContext('2d')
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height)
      ctx.drawImage(im, 0, 0)
      URL.revokeObjectURL(url)
      resolve(c.toDataURL('image/png'))
    }
    im.onerror = () => { URL.revokeObjectURL(url); reject(new Error('not an image')) }
    im.src = url
  })
}

export default function CaseReportBuilder() {
  const [files, setFiles] = useState([])              // File[] — read locally, never uploaded
  // Pictures a page is GIVEN rather than rendered — the colour map's ZBrush
  // export. Data URLs keyed by asset id, this session only: the page list
  // persists the id and the file name, never the bytes.
  const [pageAssets, setPageAssets] = useState(() => new Map())

  // The FORMAT — one of the team's report templates (formats.js). Nothing is
  // chosen by default: the first thing the operator does is say which report
  // this is, and every page offered after that is that template's own.
  const [template, setTemplate] = useState(null)
  const [picks, setPicks] = useState({})
  // Which mesh (or polygroup inside it) is a printed guide or splint. The engine
  // refuses to guess this — see PartTypes.jsx — so it is the operator's answer,
  // and like `picks` it is a DRAFT input rather than a page edit.
  const [parts, setParts] = useState({})
  // meshId → role. The classifier reads the filename, and the team's exports
  // often carry no descriptor (`Page 13.OBJ`), so a printed splint arrives as
  // role 'other' and renders bone. Nothing in the FILE can say otherwise — the
  // polypaint was measured and is blank on anatomy and appliances alike — so the
  // operator says so here. See buildLibrary().
  const [roleOverrides, setRoleOverrides] = useState({})
  const setRole = (meshId, role) => setRoleOverrides((p) => {
    const next = { ...p }
    if (role) next[meshId] = role; else delete next[meshId]
    return next
  })
  const [meta, setMeta] = useState({
    surgeonName: '', patientName: '', hospitalName: '', caseId: '', reportVersion: 'V1.0',
    // Explicitly false, not absent. An ABSENT value falls through to the template
    // geometry, which is `true` for the orthognathic report — so leaving it unset
    // would show an unticked box over a PDF that still carried page numbers.
    showPageNumbers: false,
  })

  // ── orbital template inputs ────────────────────────────────────────────────
  // The CT block the reference's page 1 prints, the quality waiver, and the
  // per-mesh volume analysis. All three are things draftReport is GIVEN — it is
  // pure and reads no files — so the collecting happens here.
  // Held WITH the case it belongs to, so one case's CT data can never print on
  // another's report (ctInfoFor). `ctRef` lets the lookup read the held fields
  // as rendered, for the status message — a state updater may run too late.
  const [allFormats, setAllFormats] = useState(false)
  const [ct, setCt] = useState({ caseKey: '', info: {} })
  const ctCase = String(meta.caseId ?? '').trim()
  const ctInfo = ctInfoFor(ct, ctCase)
  const ctRef = useRef(ct)
  ctRef.current = ct
  // Held WITH its case, exactly as the CT block is. A waiver is Osteo3d's own
  // statement about ONE scan: left as plain state it stayed ticked when the
  // operator typed the next Case ID, and case 1's notes printed on case 2's page.
  const [waiverHeld, setWaiverHeld] = useState({ caseKey: '', waived: false, notes: '' })
  const waiver = waiverHeld.caseKey === ctCase
    ? { waived: waiverHeld.waived, notes: waiverHeld.notes }
    : { waived: false, notes: '' }
  const setWaiver = (fn) => setWaiverHeld((h) => {
    const cur = h.caseKey === ctCase ? h : { caseKey: ctCase, waived: false, notes: '' }
    return { ...cur, ...fn(cur), caseKey: ctCase }
  })
  // `undefined` derives the old-scan flag from the dates; true/false is the
  // operator overriding it. Not a boolean, because "not stated" and "no" are
  // different answers and only one of them should be overridable.
  const [oldScanOverride, setOldScanOverride] = useState(undefined)
  // 'role/stage' → {cc, watertight, …}. Measured off the files, cached by
  // name|size|mtime exactly as the render cache is.
  const [volumes, setVolumes] = useState({})
  const [volumeState, setVolumeState] = useState('idle')   // idle|measuring|ready|error
  const [volumeErrors, setVolumeErrors] = useState([])
  // The portal's CT metadata for this Case ID. 'idle' until a Case ID exists.
  const [ctFetch, setCtFetch] = useState({ status: 'idle', message: '' })
  // A NUDGE, deliberately not part of `waiver`. `waiver` is document data and
  // sits in the build stamp, so folding a UI hint into it would discard a built
  // PDF every time the hint appeared — an invalidation with no change behind it.
  const [waiverHint, setWaiverHint] = useState(false)
  const volumeCacheRef = useRef(new Map())

  // The volume analysis and the waiver belong to the design confirmation
  // format, whose orbital pages need them.
  const isOrbital = formatFor(template)?.recipes === 'orbital'

  // The same panel STL/OBJ to PDF uses. This tool rendered at whatever REPORT_LOOK
  // said with no way to adjust it, so the operator could tune a preview PDF and not
  // the case report it was a preview OF.
  const reportLook = useReportLook()

  const [logo, setLogo] = useState(null)
  const [logoAspect, setLogoAspect] = useState(3)
  const [busy, setBusy] = useState('')
  const [progress, setProgress] = useState(null)
  const [error, setError] = useState('')
  const [pdfUrl, setPdfUrl] = useState(null)
  const [built, setBuilt] = useState(null)            // {pages, warnings, blob}
  const pdfUrlRef = useRef(null)
  // Bumped whenever the inputs change. A render takes tens of seconds; without
  // this, a build started before an edit lands afterwards and presents a PDF of
  // the OLD inputs as the current report.
  const runRef = useRef(0)
  // The last render, with the key it was rendered for. Lets a caption or
  // annotation change rebuild the PDF in seconds instead of re-rendering every
  // mesh. NOT state: reusing it must never itself trigger a re-render.
  const renderedRef = useRef(null)
  const pageRenderRef = useRef(null)                  // single pages' render cache
  const [pagePreview, setPagePreview] = useState(null) // {instanceId, url} of one page

  useEffect(() => {
    // The case report's logo is the master template's own layer (template.js CHASSIS.logo).
    fetch(CHASSIS.logo.asset ?? '/osteo3d-logo.png').then((r) => r.blob()).then((b) => {
      const img = new Image()
      const url = URL.createObjectURL(b)
      img.onload = () => { setLogoAspect(img.naturalWidth / img.naturalHeight || 3); URL.revokeObjectURL(url) }
      img.src = url
      const fr = new FileReader()
      fr.onload = () => setLogo(fr.result)
      fr.readAsDataURL(b)
    }).catch(() => {})
  }, [])

  // Revoke the previous preview URL rather than leaking one per rebuild.
  useEffect(() => () => { if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current) }, [])


  // The content-hash match against the planning engine's report, when one is loaded. Declared
  // before the library memo because its overrides feed it; computed by the effect further down.
  const [speGeo, setSpeGeo] = useState(null)
  const [speHashing, setSpeHashing] = useState(false)
  const hashCacheRef = useRef(new Map())

  // The library, and therefore the draft, are derived — never stored. A stale
  // draft sitting beside a changed file list is exactly the kind of quiet
  // mismatch this tool is supposed to eliminate.
  const library = useMemo(
    () => buildLibrary(
      files.filter((f) => MESH_RE.test(f.name)).map((f) => ({ name: f.name, handle: f })),
      { origin: 'upload', roleOverrides, speOverrides: speGeo?.overrides ?? {} },
    ),
    [files, roleOverrides, speGeo],
  )

  // The planning engine's report.json, when the operator adds one — or when it is simply IN the
  // picked case folder, where the engine writes it. Two things are consumed, both without
  // recomputation: `meta.cephalometry` travels into the draft VERBATIM (the numbers a surgeon
  // confirmed on screen), and `library` — the plan's mesh manifest with the sidecar's content
  // hashes — is matched against the supplied files by sha256 (speGeometry.js), which both PROVES
  // the folder holds the exact planned geometry and classifies it by identity instead of filename.
  const [speCeph, setSpeCeph] = useState(null)
  const [speDoc, setSpeDoc] = useState(null)
  const [speNote, setSpeNote] = useState(null)
  // The id this session PREFILLED (as opposed to one the operator typed): only a prefilled id may
  // be cleared automatically when the report that supplied it goes away.
  const prefilledIdRef = useRef(null)
  // 🔴 Review finding: picking a folder WITHOUT a report used to leave the previous report's
  // analysis, plan and prefilled case id in state — a wrong patient's cephalometry on the next
  // case's PDF. A folder pick is a case boundary; everything derived from the old report resets.
  const clearSpeReport = () => {
    setSpeDoc(null); setSpeCeph(null); setSpeGeo(null); setSpeNote(null)
    setMeta((m) => (prefilledIdRef.current && String(m.caseId ?? '').trim() === prefilledIdRef.current
      ? { ...m, caseId: '' } : m))
    prefilledIdRef.current = null
  }
  const onSpeReport = async (file) => {
    if (!file) return
    try {
      const doc = JSON.parse(await file.text())
      const ceph = doc?.meta?.cephalometry
      const lib = speLibrary(doc)
      if (!hasCephAnalysis(ceph) && !lib.length) {
        clearSpeReport()
        setSpeNote({ ok: false, text: 'That file carries no cephalometric analysis and no mesh '
          + "library — expected the planning engine's report.json." })
        return
      }
      setSpeCeph(hasCephAnalysis(ceph) ? ceph : null)
      setSpeDoc(doc)
      // The report identifies the case by ID (it carries no names). Prefill ONLY an empty field:
      // the operator's own entry wins, and the id keys this tool's saved edits — clobbering it
      // would silently detach them. An unverified id is treated as operator-entered, so it lands
      // in the same editable field either way; the note below says which kind arrived.
      const rid = String(doc?.meta?.caseId ?? '').trim()
      if (rid) setMeta((m) => {
        if (String(m.caseId ?? '').trim()) return m
        prefilledIdRef.current = rid
        return { ...m, caseId: rid }
      })
      const cogs = ceph?.cogs
      setSpeNote({ ok: true,
        text: (hasCephAnalysis(ceph)
          ? `Cephalometry loaded: ${ceph.angles
              ? `SNA ${ceph.angles.SNA} / SNB ${ceph.angles.SNB} / ANB ${ceph.angles.ANB}`
              : 'no classic trio (refused or locked) - the measured analysis still travels'}`
            + (cogs?.measured?.length
              ? ` · ${cogs.measured.length} Burstone measurements vs ${cogs.set?.label ?? 'no set chosen'}`
              : '')
            + (ceph.frontal?.measured?.length ? ` · ${ceph.frontal.measured.length} frontal` : '')
          : 'Report loaded — it carries no cephalometric analysis')
          + (lib.length ? ` · plan references ${lib.length} mesh${lib.length === 1 ? '' : 'es'}` : '')
          + (String(doc?.meta?.caseId ?? '').trim()
            ? ` · case ${doc.meta.caseId}${doc.meta.caseIdVerified ? '' : ' (not portal-verified)'}`
            : '') })
    } catch {
      clearSpeReport()
      setSpeNote({ ok: false, text: 'Could not read that file as JSON.' })
    }
  }

  // Match the folder's meshes to the plan's library by content hash. Hashing reads whole files,
  // so it only touches candidates — files whose SIZE equals some plan entry's (a different size
  // guarantees a different hash) — and caches by name/size/mtime so re-renders re-hash nothing.
  // (speGeo itself is declared above the library memo, which consumes its overrides.)
  useEffect(() => {
    const lib = speLibrary(speDoc)
    const meshFiles = files.filter((f) => MESH_RE.test(f.name))
    if (!lib.length || !meshFiles.length) {
      setSpeGeo(null)
      // An aborted previous run's finally is alive-guarded and never fires — without this reset a
      // mid-hash switch to a plain folder left speHashing latched true and the Build button
      // bricked on 'Verifying…' forever (re-review finding).
      setSpeHashing(false)
      return undefined
    }
    let alive = true
    ;(async () => {
      setSpeHashing(true)
      try {
        // libraryIds is buildLibrary's OWN id assignment, exported — review finding: this used to
        // re-run buildLibrary and rely on a comment that the ids match the memo's.
        const ids = libraryIds(meshFiles.map((f) => f.name))
        const sized = meshFiles.map((f, i) => ({ id: ids[i], name: f.name, size: f.size, handle: f }))
        const wanted = new Set(hashCandidates(lib, sized))
        // Three in flight — review finding: strictly serial hashing put the SUM of per-file
        // read+digest times behind the disabled Build button. Bounded at 3 so at most three
        // candidates' full bytes are resident at once (a candidate can be 100 MB).
        const queue = sized.filter((m) => wanted.has(m.id))
        const hashed = []
        const hashOne = async (m) => {
          const key = `${m.name}:${m.handle.size}:${m.handle.lastModified}`
          let h = hashCacheRef.current.get(key)
          if (!h) {
            h = await sha256Hex(await m.handle.arrayBuffer())
            hashCacheRef.current.set(key, h)
          }
          if (alive) hashed.push({ id: m.id, name: m.name, sha256: h })
        }
        let next = 0
        await Promise.all(Array.from({ length: Math.min(3, queue.length) }, async () => {
          while (alive && next < queue.length) await hashOne(queue[next++])
        }))
        if (alive) setSpeGeo(matchGeometry(lib, hashed))
      } catch (e) {
        // A failed read (an iCloud dataless placeholder, a yanked drive) must not become an
        // unhandled rejection with the match silently absent: say so, and leave speGeo null so
        // the role path decides — never a partial or stale match presented as verification.
        if (alive) {
          setSpeGeo(null)
          setSpeNote({ ok: false, text: 'Could not read a mesh while verifying the folder '
            + `against the plan (${e?.message ?? e}). No content verification was done - `
            + 'check the files are fully downloaded and re-pick the folder.' })
        }
      } finally {
        if (alive) setSpeHashing(false)
      }
    })()
    return () => { alive = false }
  }, [speDoc, files])

  // Measure the orbital volume meshes. Keyed on the files themselves, so a
  // caption edit or a meta keystroke never re-reads 25 MB of OBJ; the cache
  // makes a re-pick of the same folder instant.
  //
  // Runs only for the orbital template — no other report quotes a volume, and
  // reading every mesh of every case would cost seconds for nothing.
  const volumeKey = useMemo(
    () => (isOrbital
      ? JSON.stringify(library
        .filter((m) => ORBITAL_VOLUME_ROLES.includes(m.role))
        .map((m) => [m.id, m.role, m.stage, m.handle?.size ?? 0, m.handle?.lastModified ?? 0]))
      : ''),
    [isOrbital, library],
  )
  useEffect(() => {
    if (!isOrbital) { setVolumes({}); setVolumeState('idle'); setVolumeErrors([]); return undefined }
    const needed = library.filter((m) => ORBITAL_VOLUME_ROLES.includes(m.role))
    if (!needed.length) { setVolumes({}); setVolumeState('idle'); setVolumeErrors([]); return undefined }
    let alive = true
    setVolumeState('measuring')
    ;(async () => {
      try {
        const out = await analyzeVolumeMeshes(library, ORBITAL_VOLUME_ROLES,
          { cache: volumeCacheRef.current })
        if (!alive) return
        setVolumes(out.volumes)
        setVolumeErrors(out.errors)
        // A file that would not open is an ERROR, not an absent volume: the
        // recipe would otherwise skip page 3 saying "volumes not computed",
        // which reads as a case without volumes rather than a file that failed.
        setVolumeState(out.errors.length ? 'error' : 'ready')
      } catch (e) {
        if (!alive) return
        setVolumes({})
        setVolumeErrors([`Could not measure the orbital volumes (${e?.message ?? e}).`])
        setVolumeState('error')
      }
    })()
    return () => { alive = false }
  }, [isOrbital, volumeKey, library])

  /**
   * Pull the CT block from the portal by Case ID.
   *
   * The same call the STL/OBJ tool has used for a year
   * (PreviewPage.jsx → getReportMeta), and the endpoint already MERGES SSE's
   * own `.sse-meta.json` sidecar server-side, so one request covers both the
   * portal's derivation and the series SSE actually reconstructed.
   *
   * Three things inherited from that implementation because each one was a
   * defect there first:
   *
   *  · a 500 ms debounce, so it does not fire per keystroke;
   *  · a `cancelled` flag — the cleanup only cancels the timer, so without this
   *    a slow reply for case A lands after case B and overwrites it;
   *  · `empty` as a state distinct from `error`, because the backend answers
   *    200 with empty objects both for "no DICOM" and for a Case ID that does
   *    not exist, and those need different actions from the operator.
   *
   * The merge itself is in ctAutofill.js, where it is tested: the operator
   * always wins and only empty fields are filled. A reply CAN land after they
   * have corrected a field, and silently replacing that correction is how the
   * portal's value would reach the document under their nose.
   */
  useEffect(() => {
    const cid = String(meta.caseId ?? '').trim()
    // The hint is about one case's scan; it does not carry to the next.
    setWaiverHint(false)
    if (!cid) { setCtFetch({ status: 'idle', message: '' }); return undefined }
    let cancelled = false
    setCtFetch({ status: 'loading', message: 'Looking up CT data…' })
    const t = setTimeout(() => {
      getReportMeta(cid).then((r) => {
        if (cancelled) return
        const fetched = r.data?.ct_info ?? {}
        const status = ctFetchStatus(fetched)
        if (status !== 'ok') {
          setCtFetch({ status, message: ctFetchMessage(status) })
          return
        }
        // The counts come from the fields as rendered; the change itself is
        // applied against the latest. The merge is pure, so running it twice
        // is safe.
        const summary = mergeCtInfo(ctInfoFor(ctRef.current, cid), fetched)
        setCt((h) => ({ caseKey: cid, info: mergeCtInfo(ctInfoFor(h, cid), fetched).ctInfo }))
        // Suggested, never set: the waiver is a statement Osteo3d makes about
        // its own work, so it stays the operator's to make. Only nudged while
        // they have not touched it.
        setWaiverHint(waiverSuggested(fetched))
        setCtFetch({ status: 'ok', message: ctFetchMessage('ok', summary) })
      }).catch(() => {
        if (cancelled) return
        setCtFetch({ status: 'error', message: ctFetchMessage('error') })
      })
    }, 500)
    return () => { cancelled = true; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.caseId])

  // A settled Case ID, not every keystroke. The field is a controlled input, so
  // "O3D-2026-…" arrives one character at a time; a page list keyed on each of
  // those would be a new list per keystroke. The CT lookup already waits the
  // same 500 ms for the same reason.
  const [settledCaseId, setSettledCaseId] = useState('')
  useEffect(() => {
    const id = String(meta.caseId ?? '').trim()
    const t = setTimeout(() => setSettledCaseId(id), 500)
    return () => clearTimeout(t)
  }, [meta.caseId])

  // THE BOUNDARY IS AN ACT, NOT A GUESS. An earlier revision cleared the files
  // whenever the settled Case ID changed. A timer cannot tell "corrected a typo
  // in this case's ID" from "moved to another case", and it hung four
  // un-undoable actions on that guess: pausing half a second part-way through
  // typing an ID destroyed the files, the page list, the role corrections and
  // the loaded plan, with nothing on screen saying why. File handles cannot be
  // recovered, so every page had to be browsed for again.
  //
  // So nothing is discarded on its own. Work is carried forward to the case the
  // operator names (usePageBasket adopts it when that case holds no list of its
  // own), and "Clear" below is the one place work is thrown away — an explicit
  // act, on a list the operator is looking at.
  const startAnotherCase = () => {
    setFiles([])
    setRoleOverrides({})
    clearSpeReport()
  }

  const phiWarn = useMemo(() => phiKeyWarning(files.map((f) => f.name)), [files])

  // What the files say this report is. A suggestion, never a decision: it is
  // offered while the chosen format disagrees, and doing nothing ignores it.
  const suggestion = useMemo(() => suggestFormat(library), [library])
  const suggestedElsewhere = suggestion && suggestion.id !== formatFor(template)?.id

  // The pages of this report, in order — page mode (ADR 0015). Starts EMPTY;
  // the operator adds pages one at a time. Restored per case like the edits.
  const pageBasket = usePageBasket(settledCaseId, template)
  // A folder whose meshes are named by page ("Page 03 - …") already states its
  // own page order, and keeps the route it has always had (pageDraft.js).

  // Everything the draft reads besides the page list — shared by the report
  // itself and by the "can this case fill it?" line under Add a page, so the
  // two cannot answer differently.
  const draftInputs = useMemo(() => ({
    template, picks, parts, ceph: speCeph,
    // The old-scan flag travels INSIDE meta, so it is part of the build stamp
    // and an override invalidates a built PDF like any other input.
    // An EMPTY version box prints the template's version rather than no version
    // at all: the field is a place to override, not a way to drop it.
    meta: {
      ...meta,
      reportVersion: String(meta.reportVersion ?? '').trim() || chromeFor(template)?.version || 'V1.0',
      oldScan: oldScanOverride,
    },
    ctInfo, volumes, waiver: waiver.waived || waiver.notes ? waiver : null,
    // The plan's scene compositions become the anatomy pages when the matches exist.
    spe: (speDoc && speGeo?.matched?.length) ? { doc: speDoc, matched: speGeo.matched } : null,
  }), [template, picks, parts, speCeph, meta, oldScanOverride, ctInfo, volumes, waiver, speDoc, speGeo])

  const format = formatFor(template)
  const formatReady = !!format && buildable(format)
  // The pages of the REPORT: the template's, less the ones this format encloses
  // rather than generates. `formatPages.js` drops the same set at the one place
  // a format becomes a page list, so this count, the page list, the draft and
  // the footer's "of N" cannot disagree with each other.
  const reportedPages = format?.pages.filter((p) => !p.omit) ?? []
  const omitted = omittedPages(template)

  // The chosen template's version travels in the meta for the hidden PDF stamp only;
  // it is never printed and never typed (DK-1, 26 Sep).
  useEffect(() => {
    const v = chromeFor(template)?.version
    if (v) setMeta((m) => ({ ...m, reportVersion: v }))
  }, [template])

  // A case built in another format: follow the list that is stored for it,
  // rather than starting an empty one over the operator's work. Said on screen,
  // because the picker moving under the operator's hand needs a reason.
  const [followed, setFollowed] = useState(null)
  useEffect(() => {
    if (pageBasket.storedFormat && pageBasket.storedFormat !== template) {
      setFollowed({ from: template, to: pageBasket.storedFormat })
      setTemplate(pageBasket.storedFormat)
    }
  }, [pageBasket.storedFormat, template])
  useEffect(() => { setFollowed(null) }, [meta.caseId])
  // Once the operator picks a format themselves, the message has been acted on.
  useEffect(() => { setFollowed((f) => (f && f.to !== template ? null : f)) }, [template])

  const draft = useMemo(() => {
    // Text and CT pages need no mesh at all, so a list with pages in it drafts
    // whether or not a case folder has been picked.
    if (!formatReady) return null
    if (!library.length && !pageBasket.basket?.entries.length) return null
    try {
      if (!pageBasket.basket) return null
      return draftPages(library, { ...draftInputs, pages: withLiveAssets(pageBasket.basket.entries, pageAssets) })
    } catch (e) {
      return { error: e.message }
    }
  }, [library, template, draftInputs, pageBasket.basket, pageAssets])

  // What "Add a page" offers: the generic types (every template) first, then
  // this template's suggested pages.
  const offered = useMemo(
    () => (formatReady ? offeredPages(template) : { template: [], generic: [], other: [], held: null }),
    [template, formatReady],
  )

  // Which pages in the list produced nothing. Read from the DRAFT's own
  // per-page report (`built` is the number of sheets that page added), not
  // counted a second way here — two counters on one screen is how the tool came
  // to say "Pages in this report (20)" and "Draft — 3 pages" at the same time.
  const unbuilt = useMemo(() => {
    const entries = pageBasket.basket?.entries ?? []
    if (!draft?.pages || !entries.length) return []
    const byId = new Map(draft.pages.map((p) => [p.instanceId, p]))
    return entries
      .filter((e) => !(byId.get(e.instanceId)?.built > 0))
      .map((e) => offered.template.concat(offered.generic, offered.other)
        .find((t) => t.id === e.typeId)?.listTitle ?? e.typeId)
  }, [draft, pageBasket.basket, offered])
  // Before a SUGGESTED page is added: would it build for this case, and if not,
  // why? Drafted alone, with the report's own inputs — the same sentence the
  // page would carry once added. Generic pages need the operator's picks, so
  // they are not probed.
  const availability = useMemo(() => {
    if (!library.length) return {}
    const out = {}
    for (const t of [...offered.template, ...offered.other].filter((t) => !t.hand && !t.genericId)) {
      try {
        const d = draftPages(library, { ...draftInputs, pages: [{ instanceId: 'probe', typeId: t.id }] })
        const skip = d.notes.find((n) => n.kind === 'skipped')
        out[t.id] = d.pages?.[0]?.built
          ? { ok: true }
          : { ok: false, reason: (skip?.reason ?? 'not built').replace(/^not built — /, '') }
      } catch (e) {
        out[t.id] = { ok: false, reason: e.message }
      }
    }
    return out
  }, [library, offered, draftInputs])

  // The operator's captions and annotations, keyed by a CONTENT-derived page key
  // (edits.js). They live outside the document because the document is re-derived
  // on every change; re-applying them is what makes them survive that.
  //
  // Restored and persisted per case by useCaseEdits — the SAME hook the UI
  // harness drives, so what is tested is what ships.
  const { caseKey, edits, setEdits, restored, saveState, clear: clearCaseEdits } =
    useCaseEdits(meta.caseId)

  // `picks` and `parts` survive a refresh, keyed by case, like the page edits.
  // They are DRAFT inputs rather than page edits, so they get their own store
  // — see editStore.js. Restoring is guarded on the case id changing, and the
  // stored payload re-checks it, because putting one patient's part
  // declarations onto another's report would recolour a clinical document.
  const draftCase = useRef(null)
  const restoredCase = useRef(null)
  useEffect(() => {
    const id = String(meta.caseId ?? '').trim()
    if (draftCase.current === id) return
    draftCase.current = id
    const saved = id ? loadDraftInputs(id) : null
    setPicks(saved?.picks ?? {})
    setParts(saved?.parts ?? {})
    restoredCase.current = id
  }, [meta.caseId])

  useEffect(() => {
    const id = String(meta.caseId ?? '').trim()
    // Only once this case's own values are LOADED. `draftCase.current` is set by
    // the restore effect in the same commit, so comparing against it is always
    // true and guarded nothing — the save ran with pre-restore state and cleared
    // the stored entry before rewriting it. A separate flag is the real guard.
    if (!id || restoredCase.current !== id) return
    saveDraftInputs(id, { picks, parts })
  }, [meta.caseId, picks, parts])

  // Everything downstream — render, build, preview — uses the EDITED document.
  // Rendering the raw draft and building the edited one would put the operator's
  // page list and the pictures out of step, which is the class of mismatch this
  // tool exists to remove.
  const edited = useMemo(() => {
    if (!draft?.doc) return null
    return applyEdits(draft.doc, edits)
  }, [draft, edits])

  // A built report belongs to the inputs it was built from. Change any of them
  // and the preview, the page count and the Download button must go — otherwise
  // the panel keeps offering a finished-looking PDF of something else, which is
  // the same "looks finished, isn't" failure this tool exists to remove.
  // `edits` is IN the stamp: change a caption and the built PDF must be
  // invalidated too, or the panel keeps offering a finished-looking download of
  // the document as it was before the edit.
  // `m.role` AND `m.stage` are in the stamp, not just `m.id`: correcting a role
  // changes the colours and the pages while every id stays exactly the same, and
  // the stage now follows the role, so a role change can move the page count with
  // an otherwise byte-identical stamp. Measured: clearing an override via "Detected"
  // took a draft from 8 pages back to 6 while the id list never moved, leaving the
  // pre-correction PDF on offer — the failure this stamp exists to prevent.
  // `volumes` and `volumeState` are IN the stamp, not just the files they come
  // from. Volume analysis is ASYNC: a build started before it lands would
  // produce a document with no legend, and without these the stamp would never
  // change when the figures arrived, leaving that document on offer as current.
  // `ctInfo`, `waiver` and the old-scan override are here for the same reason as
  // `meta` — they change what is printed.
  // Pure, and in its own module, because what keeps going wrong is an input
  // nobody listed — and that cannot be tested inside a useMemo. buildStamp.test
  // walks every declared input and requires each to move the stamp, so adding
  // one here without declaring it there fails. See buildStamp.js.
  const stamp = useMemo(
    () => buildStamp({
      template, picks, parts, meta, edits, renderOpts: reportLook.renderOpts,
      ctInfo, waiver, oldScanOverride, volumes, volumeState, library,
      basket: pageBasket.basket,
    }),
    [template, picks, parts, meta, edits, reportLook.renderOpts,
      ctInfo, waiver, oldScanOverride, volumes, volumeState, library,
      pageBasket.basket],
  )
  useEffect(() => {
    runRef.current += 1
    setBuilt(null)
    setError('')
    if (pdfUrlRef.current) { URL.revokeObjectURL(pdfUrlRef.current); pdfUrlRef.current = null }
    setPdfUrl(null)
    // The single-page preview goes too — the same "looks finished, isn't" risk.
    setPagePreview((prev) => { if (prev?.url) URL.revokeObjectURL(prev.url); return null })
  }, [stamp])

  // Notes are shown per page, in the row they belong to (PageList), so they are
  // not gathered here any more.
  // ALL conflicts, resolved or not. Filtering to the unresolved ones made the
  // whole panel vanish the instant an operator picked a mesh: no confirmation of
  // what they had chosen and no way to change their mind. A choice that
  // disappears when you make it is not a choice you can check.
  const conflicts = draft?.conflicts ?? []
  const unresolved = conflicts.filter((c) => !c.chosen)

  // Bytes are read straight off the operator's disk and never leave the browser.
  async function loadBytes(part) {
    const h = part.handle
    if (!h) throw new Error(`No file handle for "${part.name}"`)
    if (typeof Blob === 'undefined' || !(h instanceof Blob)) {
      throw new Error(`"${part.name}" is not a local file — re-pick the case folder.`)
    }
    return new Uint8Array(await h.arrayBuffer())
  }

  // Render a document — or reuse the last render of `cacheRef` when every
  // picture would be the same. The report and single pages keep separate caches,
  // so previewing one page never throws away the report's render.
  async function renderSource(source, sceneParts, live, cacheRef = renderedRef) {
    const rendered = await renderWithCache({
      doc: source,
      sceneParts,
      library,
      renderOpts: { ...reportLook.renderOpts, boneColor: BONE, background: 'white' },
      cache: cacheRef.current,
      loadBytes,
      renderReport,
      onProgress: (done, total, label) => { if (live()) setProgress({ done, total, label }) },
    })
    if (live()) cacheRef.current = rendered.cache
    return rendered
  }

  // One page on its own — to look at (Preview) or to merge by hand (Export).
  // Drafted and rendered ALONE, as the operator asked for it: one page never
  // waits on the whole report. Its pictures are framed for that page, and it
  // says "Images are not to scale"; it carries no page number, because its
  // place in the final document is unknown (looseSheetMeta).
  async function onePage(instanceId, mode) {
    const entry = pageBasket.basket?.entries.find((e) => e.instanceId === instanceId)
    if (!entry || !draft || draft.error) return
    // `handSheets` — a page the Builder cannot finish still downloads, as a
    // working sheet for the team to draw on in GIMP. Set HERE and nowhere else:
    // the report itself must not carry a half-finished page.
    const one = draftPages(library, {
      ...draftInputs, meta: looseSheetMeta(draftInputs.meta),
      pages: withLiveAssets([entry], pageAssets), handSheets: true,
    })
    const sheet = applyEdits(one.doc, edits).doc
    if (!sheet.pages.length) { setError('That page was not built, so there is nothing to show.'); return }
    const run = runRef.current
    const live = () => runRef.current === run
    setError(''); setBusy('render')
    try {
      const rendered = await renderSource(sheet, one.sceneParts, live, pageRenderRef)
      if (!live()) return
      setBusy('pdf')
      const { doc: pdf } = await buildCaseReport(sheet, {
        resolveImage: makeResolveImage(rendered.views, Object.fromEntries(pageAssets)), logo, logoAspect,
      })
      if (!live()) return
      const url = URL.createObjectURL(pdf.output('blob'))
      if (mode === 'export') {
        const a = document.createElement('a')
        a.href = url
        a.download = `${(meta.caseId || 'case').replace(/[^\w-]/g, '')}_page_${entry.typeId}_${instanceId}.pdf`
        a.click()
        URL.revokeObjectURL(url)
      } else {
        setPagePreview((prev) => {
          if (prev?.url) URL.revokeObjectURL(prev.url)
          return { instanceId, url }
        })
      }
    } catch (e) {
      if (live()) setError(e.message || String(e))
    } finally { if (live()) { setBusy(''); setProgress(null) } }
  }

  async function build() {
    if (!draft || draft.error || !edited) return
    const run = runRef.current
    const live = () => runRef.current === run
    // ONE document from here on. Rendering the draft and writing the edited copy
    // would let the pictures and the page list disagree.
    const source = edited.doc
    setError(''); setBuilt(null); setBusy('render'); setProgress({ done: 0, total: source.scenes.length * 2, label: '' })
    try {
      // Captions, annotations and page ORDER change no pixel of any render, and a
      // full render of a real case is ~110 s — so a render is reused whenever the
      // pictures would be identical. What decides that lives in renderCache.js,
      // pure and tested, because the key written inline here had two defects: it
      // could not see which VIEWS each scene needs (a Map serialises as `{}`), so
      // a page asking for a view never drawn got an empty box; and it named
      // scenes by id, so reordering pages re-rendered the whole case.
      //
      // `boneColor` and `background` are PINNED rather than taken from the panel,
      // and both matter. The renderer decides whether a part colour is a
      // deliberate tint by comparing it against `boneColor`; a case report's parts
      // all carry colours from `colorForRole`, so moving that reference off BONE
      // makes every anatomy part read as tinted, discards its ZBrush polypaint and
      // multiplies the matcap by #CFC1A7 — the ~18% olive-taupe darkening this
      // option set was written to end. And a case report prints on white A4, where
      // a black ground is never right. The panel hides both controls here to match.
      const rendered = await renderSource(source, draft.sceneParts, live)
      if (!live()) return
      setBusy('pdf')
      const { doc: pdf, warnings } = await buildCaseReport(source, {
        resolveImage: makeResolveImage(rendered.views, Object.fromEntries(pageAssets)), logo, logoAspect,
      })
      if (!live()) return
      const blob = pdf.output('blob')
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current)
      const url = URL.createObjectURL(blob)
      pdfUrlRef.current = url
      setPdfUrl(url)
      // Relief warnings ride the SAME channel as the document warnings rather than getting a
      // banner of their own — one place the operator looks before sending a report out.
      // They come from the render, which is cached across rebuilds, so they must be read from
      // `rendered` and not from this build's own return value.
      setBuilt({
        pages: pdf.getNumberOfPages(),
        warnings: [...(rendered.reliefWarnings ?? []), ...warnings],
        blob,
        // Kept for the PNG exports (team, 15 Oct 2026). The renders are already in hand here;
        // re-rendering them for an export would be a second pass over the same meshes.
        views: rendered.views,
        sceneNames: Object.fromEntries((source.scenes ?? []).map((sc) => [sc.id, sc.name])),
      })
    } catch (e) {
      if (live()) setError(e.message || String(e))
    } finally { if (live()) { setBusy(''); setProgress(null) } }
  }

  // One empty branded sheet, for the hand-built pages that are still merged into
  // the PDF afterwards. Deliberately NOT a page of the report, and not gated on a
  // case folder — it needs no mesh render, only the fields and the logo. It DOES
  // need a format, because the sheet carries that template's chrome. Downloads
  // straight out rather than going through `built`, which holds the report and
  // must not be displaced.
  async function buildBlank() {
    setError('')
    try {
      const { doc: pdf } = await buildBlankPage(meta, { template, logo, logoAspect })
      const url = URL.createObjectURL(pdf.output('blob'))
      const a = document.createElement('a')
      a.href = url
      a.download = `${(meta.caseId || 'case').replace(/[^\w-]/g, '')}_blank_page.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e.message || String(e))
    }
  }

  /** What the page being previewed is called — its template page's name, or the type's. */
  function previewPageTitle() {
    const typeId = pageBasket.basket?.entries
      .find((e) => e.instanceId === pagePreview?.instanceId)?.typeId
    const type = [...offered.template, ...offered.generic, ...offered.other].find((t) => t.id === typeId)
    return type?.listTitle ?? type?.title ?? 'page'
  }

  function download() {
    if (!built) return
    const a = document.createElement('a')
    a.href = pdfUrl
    // The document is named for what it IS. A design confirmation arriving as
    // "…_case_report.pdf" would be filed as a case report by whoever receives
    // it, and the two are different deliverables to a surgeon. The name comes
    // from the format (formats.js).
    a.download = reportFileName(template, meta.caseId)
    a.click()
  }

  /**
   * The PNG exports the team asked for on 15 Oct 2026:
   *   "export the final generated output as PNG image files, allowing users to access and use
   *    individual images more conveniently"
   *   "export the watermark page as a blank page with a transparent background"
   *
   * Deepak, 17 Oct: both kinds of image — the model renders AND the finished pages.
   *
   * Zipped rather than downloaded one by one: a six-view report is 40-odd files, and a browser
   * asked for 40 downloads either blocks them or buries the operator in save dialogs.
   */
  async function downloadZip(suffix, files) {
    if (!Object.keys(files).length) throw new Error('nothing to export')
    // fflate is already a dependency and already used this way by the STL clean tool.
    const { zipSync } = await import('fflate')
    const zipped = zipSync(files, { level: 6 })
    const url = URL.createObjectURL(new Blob([zipped], { type: 'application/zip' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `${exportStem(meta.caseId)}_${suffix}.zip`
    a.click()
    URL.revokeObjectURL(url)
  }

  /** The model renders themselves — no frame, no header, no watermark. */
  async function exportRenders() {
    setError('')
    if (!built?.views) { setError('Build the report first — the renders come from that build.'); return }
    setBusy('png')
    try {
      const entries = renderEntries(built.views, built.sceneNames)
      if (!entries.length) throw new Error('that build produced no renders to export')
      const files = {}
      for (const e of entries) files[e.name] = dataUrlToBytes(e.dataUrl)
      await downloadZip('renders_png', files)
    } catch (e) {
      setError(e.message || String(e))
    } finally { setBusy('') }
  }

  /** The finished pages, rasterised from the REAL PDF so they cannot disagree with it. */
  async function exportPagePngs() {
    setError('')
    if (!built?.blob) { setError('Build the report first.'); return }
    setBusy('png')
    try {
      const { rasterisePdf, canvasToPngBytes } = await import('../../lib/caseReport/pdfRaster.js')
      const bytes = new Uint8Array(await built.blob.arrayBuffer())
      setProgress({ done: 0, total: built.pages, label: 'rasterising' })
      // Encoded page by page as it is rasterised. Collecting the canvases instead would hold
      // 34.8 MB each — 0.80 GB for a 23-page fibula report — and kill the tab.
      const pad = String(built.pages).length
      const named = await rasterisePdf(bytes, {
        handler: async (canvas, n, total) => {
          setProgress({ done: n, total, label: 'rasterising' })
          return [`page-${String(n).padStart(pad, '0')}.png`, await canvasToPngBytes(canvas)]
        },
      })
      await downloadZip('pages_png', Object.fromEntries(named))
    } catch (e) {
      setError(e.message || String(e))
    } finally { setBusy(''); setProgress(null) }
  }

  /**
   * The blank sheet as a TRANSPARENT PNG, for laying over the team's own images.
   *
   * 🔴 The alpha is in the PNG and never in the PDF. The blank sheet's own PDF is unchanged: this
   * rasterises it and edits pixels. Putting transparency into the document itself is the print
   * defect of 30 Sep 2026 — see pngExport.js.
   */
  async function buildBlankOverlay() {
    setError('')
    setBusy('png')
    try {
      const [{ rasterisePdf, canvasToPngBytes }, { doc: pdf }] = await Promise.all([
        import('../../lib/caseReport/pdfRaster.js'),
        buildBlankPage(meta, { template, logo, logoAspect }),
      ])
      const bytes = new Uint8Array(await pdf.output('blob').arrayBuffer())
      const g = geometryFor(template)
      const [png] = await rasterisePdf(bytes, {
        alpha: true,
        handler: async (canvas) => {
          const ctx = canvas.getContext('2d')
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const changed = toTransparentOverlay(img, { watermark: g?.watermark, logoBox: logoBoxOf(g) })
          if (!changed.watermarkPixels) {
            throw new Error('the watermark was not found in the rasterised page, so the overlay '
              + 'would carry it as opaque grey — not exporting a sheet that would look wrong '
              + 'over an image')
          }
          ctx.putImageData(img, 0, 0)
          return canvasToPngBytes(canvas)
        },
      })
      const url = URL.createObjectURL(new Blob([png], { type: 'image/png' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `${exportStem(meta.caseId)}_blank_page_transparent.png`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e.message || String(e))
    } finally { setBusy('') }
  }

  const setM = (k, v) => setMeta((m) => ({ ...m, [k]: v }))

  /**
   * The files a page was given. They are added to the report's files (so the
   * next page can reuse one without browsing for it again) and to that page's
   * own list, which is what the page is built from.
   */
  // A page's own picture — the colour map. The black ZBrush ground is taken
  // off with the same flood fill the background-removal tool uses, then the
  // picture is laid on white, because the PDF writer flattens to JPEG and an
  // alpha channel would otherwise come out black again.
  const addImageToPage = async (instanceId, file) => {
    if (!file) return
    try {
      const id = `${instanceId}:${file.name}:${file.size}`
      const url = await imageOnWhite(await bgrDirectRemove(file, 35))
      setPageAssets((prev) => new Map(prev).set(id, url))
      pageBasket.apply((b) => setPageInputs(b, instanceId, { image: id, imageName: file.name }))
    } catch (e) {
      setError(`That image could not be read: ${e.message || e}`)
    }
  }

  const addFilesToPage = (instanceId, fileList) => {
    const picked = [...(fileList ?? [])]
    const incoming = picked.filter((f) => MESH_RE.test(f.name))
    // 🔴 A file this tool cannot use must SAY so. It used to return here, so a
    // page given the case's delivered PDF by mistake showed nothing at all —
    // no message, no change, the operator's file simply gone. Silence reads as
    // "it worked".
    if (!incoming.length) {
      setError(picked.length
        ? `Not added — ${picked.length === 1 ? 'that file is' : 'those files are'} not a `
          + `3D model. This page takes .stl or .obj files. (${picked.map((f) => f.name).join(', ')})`
        : 'No files were chosen.')
      return
    }
    setError('')
    const key = (f) => `${f.name}:${f.size}`
    const have = new Set(files.map(key))
    const next = [...files, ...incoming.filter((f) => !have.has(key(f)))]
    // Ids are derived from the file name, so they are read off a library built
    // from the files as they will be — not guessed at.
    const nextLib = buildLibrary(
      next.filter((f) => MESH_RE.test(f.name)).map((f) => ({ name: f.name, handle: f })),
      { origin: 'upload', roleOverrides, speOverrides: speGeo?.overrides ?? {} },
    )
    const wanted = new Set(incoming.map(key))
    const ids = nextLib.filter((m) => m.handle && wanted.has(key(m.handle))).map((m) => m.id)
    setFiles(next)
    const already = pageBasket.basket?.entries.find((e) => e.instanceId === instanceId)?.inputs?.meshes ?? []
    pageBasket.apply((b) => setPageInputs(b, instanceId, { meshes: [...new Set([...already, ...ids])] }))
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-4">
      {/* ── left: inputs ─────────────────────────────────────────────────── */}
      <div className="space-y-4">
        {/* What this format can and cannot do, counted from the template
            itself — so it cannot go stale the way the old fixed text did. */}
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="format-summary">
          {format ? (
            <>
              <span className="text-sm font-semibold text-amber-900 block mb-1">
                {format.label} · {format.chrome?.version?.text}
              </span>
              {/* Counted over the pages of the REPORT, not of the template.
                  The two differ: the orthognathic template carries 17 pages and
                  its own page-number layer reads "Page 09 of 14", because the
                  agreement and the two terms pages are enclosed by the team.
                  Both numbers are shown, because a report that said 14 with no
                  account of the other three reads as three pages lost. */}
              <p className="text-xs text-amber-900 leading-relaxed">
                {reportedPages.length} pages in this report:{' '}
                {reportedPages.filter((p) => !p.hand).length} built here,{' '}
                {reportedPages.filter((p) => p.hand).length} made in GIMP and merged in as now.
              </p>
              {omitted.length > 0 && (
                <p className="text-xs text-amber-900 leading-relaxed mt-1"
                  data-testid="format-omitted">
                  {omitted.length} more in the template, enclosed by the team:{' '}
                  {omitted.map((p) => p.title).join(', ')}.
                </p>
              )}
              <p className="text-xs text-amber-900 mt-2">
                Please try it on a case you have <b>already delivered</b> and compare, then tell us
                whether it saves time.
              </p>
            </>
          ) : (
            <p className="text-xs text-amber-900">
              Choose the report format first. Its pages are then listed in the template&rsquo;s order.
            </p>
          )}
        </div>

        {/* The files come PER PAGE now: each page asks for the STL/OBJ files it
            shows, so nothing is read in bulk and nothing is classified that the
            report does not use. This card keeps only the optional planning
            engine report, which belongs to the case rather than to a page. */}
        <div className={card}>
          <p className="text-xs text-gray-500 mb-2" data-testid="files-note">
            Each page asks for its own files. They are read from this computer — nothing is
            uploaded, and the meshes never leave your machine.
          </p>
          <label className="text-xs font-medium text-gray-500 block mb-1 mt-3">
            Planning engine report <span className="text-gray-400">(report.json — optional)</span>
          </label>
          <input
            type="file" accept=".json,application/json"
            onChange={(e) => onSpeReport(e.target.files?.[0])}
            className="text-sm"
          />
          {speNote && (
            <p className={`text-xs mt-1 ${speNote.ok ? 'text-green-700' : 'text-amber-700'}`}>
              {speNote.text}
            </p>
          )}
          {speHashing && (
            <p className="text-xs mt-1 text-gray-500">Verifying the folder&rsquo;s meshes against the plan&hellip;</p>
          )}
          {!speHashing && speGeo?.summary && (
            <p className={`text-xs mt-1 ${speGeo.gaps.length ? 'text-amber-700' : 'text-green-700'}`}>
              Geometry: {speGeo.summary}
            </p>
          )}
          {!speHashing && speGeo?.gaps.map((g) => (
            <p key={g} className="text-xs mt-0.5 text-amber-700">• {g}</p>
          ))}
          {phiWarn && <p className="text-xs mt-1 text-amber-700">{phiWarn}</p>}
          {!!files.length && (
            <p className="text-xs text-gray-400 mt-1" data-testid="library-count">
              {library.length} mesh{library.length === 1 ? '' : 'es'} of {files.length} file
              {files.length === 1 ? '' : 's'} on the pages of this report
              {library.length === 0 && ' — no .stl or .obj found'}
            </p>
          )}
        </div>

        <div className={card} data-testid="format-picker">
          <span className="text-sm font-semibold text-gray-700 block mb-2">Report format</span>
          {suggestedElsewhere && (
            <p className="text-[11px] text-brand-800 bg-brand-50 border border-brand-200 rounded p-2 mb-2"
              data-testid="format-suggestion">
              These files look like a{' '}
              {suggestion.because.map((r) => (ROLE_LABELS[r] ?? r).toLowerCase()).join(', ')} case —{' '}
              <b>{formatFor(suggestion.id)?.label}</b>.{' '}
              <button type="button" className="underline font-medium"
                data-testid="format-suggestion-use"
                onClick={() => setTemplate(suggestion.id)}>
                Use it
              </button>
            </p>
          )}
          {followed && (
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-2"
              data-testid="format-followed">
              This case already has pages built in{' '}
              <b>{formatFor(followed.to)?.label}</b>, so that format is selected. To build it in{' '}
              {formatFor(followed.from)?.label ?? 'another format'} instead, pick that format again —
              this case&rsquo;s page list is replaced.
            </p>
          )}
          <p className="text-[11px] text-gray-500 mb-2">
            The team&rsquo;s own templates. Its pages are then listed in the template&rsquo;s order.
          </p>
          {/* The few the team delivers most, and the rest one click away. The
              version and the page counts are not part of the choice — they are
              in the line under this card once a format is chosen. */}
          {!allFormats && (
            <div className="mb-1">
              {/* The few, plus whatever is actually selected — a format chosen
                  from the full list, followed from a stored list or accepted
                  from the suggestion must not vanish behind "Show all". */}
              {[...new Set([...COMMON_FORMATS, ...(template ? [template] : [])])]
                .map((id) => formatFor(id)).filter(Boolean).map((f) => (
                <label key={f.id} className="flex items-center gap-2 text-sm mb-1 text-gray-600">
                  <input type="radio" name="format" checked={template === f.id}
                    data-testid={`format-${f.id}`} onChange={() => setTemplate(f.id)} />
                  <span>{f.label}</span>
                </label>
              ))}
              <button type="button" className="text-xs text-brand-700 underline mt-1"
                data-testid="format-show-all" onClick={() => setAllFormats(true)}>
                Show all formats
              </button>
            </div>
          )}

          {allFormats && [['case-report', 'Case reports'], ['images', 'Images for the surgeon'], ['other', 'Other']]
            .map(([group, heading]) => (
              <div key={group} className="mb-2">
                <span className="text-[11px] font-semibold text-gray-400 block mb-0.5">{heading}</span>
                {FORMATS.filter((f) => f.group === group).map((f) => {
                  const ok = buildable(f)
                  return (
                    <label key={f.id}
                      className={`flex items-start gap-2 text-sm mb-1 ${ok ? 'text-gray-600' : 'text-gray-400'}`}>
                      <input type="radio" name="format" checked={template === f.id} disabled={!ok}
                        data-testid={`format-${f.id}`}
                        onChange={() => setTemplate(f.id)} className="mt-1" />
                      <span>
                        {f.label}
                        {f.link && (
                          <span className="block text-xs text-gray-400">
                            Made in the <a className="underline" href={f.link.to}>{f.link.label}</a> tool
                          </span>
                        )}
                        {!ok && !f.link && (
                          <span className="block text-xs text-gray-400">
                            Every page of this template is made in GIMP
                          </span>
                        )}
                      </span>
                    </label>
                  )
                })}
              </div>
            ))}
        </div>

        <div className={card}>
          <span className="text-sm font-semibold text-gray-700 block mb-2">Report fields</span>
          {/* Labelled, not just placeholdered: a placeholder vanishes the moment
              the field is filled, and these four print on every page of a
              document a surgeon signs. */}
          <div className="space-y-2">
            {[
              ['caseId', 'Case ID'],
              ['patientName', 'Patient name'],
              ['surgeonName', 'Surgeon name'],
              ['hospitalName', 'Hospital name'],
            ].map(([k, label]) => (
              <label key={k} className="block">
                <span className="text-xs font-medium text-gray-500 block mb-0.5">{label}</span>
                <input value={meta[k]} onChange={(e) => setM(k, e.target.value)}
                  placeholder={label} className={field} />
              </label>
            ))}
            {/* The report date, for EVERY format. The version has no box: nothing prints it
                (DK-1, 26 Sep) — the template's own version goes into the PDF properties as a
                hidden mark, and the operator never types one. */}
            <label className="block">
              <span className="text-xs font-medium text-gray-500 block mb-0.5">Report date</span>
              <input type="date" value={meta.dateStr ?? ''} className={field} data-testid="report-date"
                onChange={(e) => setM('dateStr', e.target.value)} />
            </label>
            {/* OFF by default while the workflow is HYBRID: hand-built pages are
                inserted into the PDF afterwards, so the tool's own count is wrong
                the moment that happens. The template geometry still records that
                the delivered reference DOES carry numbers — this only overrides
                it per report, and is reversible when the manual method retires. */}
            <label className="flex items-center gap-2 pt-1">
              <input type="checkbox" checked={meta.showPageNumbers === true}
                onChange={(e) => setM('showPageNumbers', e.target.checked)} />
              <span className="text-xs font-medium text-gray-600">Page numbers in footer</span>
            </label>
          </div>
        </div>

        {/* ── Design confirmation details: the orbital template's own inputs ──
            Only for that template. The CT block is what page 1 prints, keyed to
            sseReport's CT_FIELDS so a future autofill from the scan has somewhere
            to land — the reference document's own field names, in its order. */}
        {/* CT data, for EVERY template. Read from the portal by Case ID — the
            lookup the STL/OBJ tool has made for a year — and it feeds the CT data
            page of any report. It used to live inside the orbital panel only, so
            no other template ever received it. */}
        {formatReady && (
          <div className={card} data-testid="ct-data-panel">
            <div className="space-y-2">
          <div className="pt-2">
            <span className="text-xs font-semibold text-gray-600 block">CT data</span>
            {ctFetch.status !== 'idle' && (
              <p className={`text-[11px] mt-0.5 ${
                ctFetch.status === 'ok' ? 'text-green-700'
                  : ctFetch.status === 'loading' ? 'text-gray-500' : 'text-amber-700'}`}>
                {ctFetch.message}
              </p>
            )}
            {ctFetch.status === 'idle' && (
              <p className="text-[11px] text-gray-500 mt-0.5">
                Enter the Case ID above first. These then fill from the portal, where that
                case’s DICOM went through it. Anything you type is kept.
              </p>
            )}
          </div>
          {ORBITAL_CT_ROW_KEYS.map((key) => {
            const label = CT_FIELDS.find(([, k]) => k === key)?.[0] ?? key
            return (
              <label key={key} className="block">
                <span className="text-xs font-medium text-gray-500 block mb-0.5">{label}</span>
                <input value={ctInfo[key] ?? ''} className={field} placeholder={label}
                  disabled={!ctCase}
                  onChange={(e) => setCt((h) => ({
                    caseKey: ctCase, info: { ...ctInfoFor(h, ctCase), [key]: e.target.value },
                  }))} />
              </label>
            )
          })}
            </div>
          </div>
        )}

        {/* The quality waiver, for EVERY format. It was inside the orbital card until
            1 Oct 2026, so only the orbital report could raise one — but the master
            template reserves the slot on the source-data page of every format, and the
            fibula team asked for it (genericPages g-ct-data, template.js sourceData.waiver). */}
        <div className={card}>
          <span className="text-sm font-semibold text-gray-700 block mb-1">Quality waiver</span>
          <p className="text-[11px] text-gray-500 mb-2">
            Prints on the CT data page. A waiver is Osteo3d's own statement about the scan it
            worked from, so it is never ticked for you.
          </p>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={waiver.waived}
              onChange={(e) => { setWaiver((w) => ({ ...w, waived: e.target.checked })); setWaiverHint(false) }} />
            <span className="text-xs font-medium text-gray-600">Quality waiver</span>
          </label>
          {waiverHint && !waiver.waived && (
            <p className="text-[11px] text-amber-700 mt-1">
              The portal reports this study as more than 30 days old. A waiver is usually
              raised for those — it is not ticked for you.
            </p>
          )}
          {waiver.waived && (
            <label className="block mt-2">
              <span className="text-xs font-medium text-gray-500 block mb-0.5">Waiver notes</span>
              <textarea value={waiver.notes} rows={4} className={field}
                placeholder="Why the waiver applies — prints under “Quality waiver: Yes”."
                onChange={(e) => setWaiver((w) => ({ ...w, notes: e.target.value }))} />
            </label>
          )}
        </div>

        {isOrbital && (
          <div className={card}>
            <span className="text-sm font-semibold text-gray-700 block mb-1">
              Design confirmation details
            </span>
            <p className="text-[11px] text-gray-500 mb-2">
              The CT block and the waiver print on page 1. Expected filenames, the old-scan rule
              and the known differences from the hand-built document are in
              <strong> ORBITAL-REPORT-TEAM-GUIDE.md</strong>.
            </p>
            <div className="space-y-2">
              {/* The version and the report date are in Report fields, for every
                  format. The report date decides whether the scan counts as old —
                  the reference's own case is 8 days from study to DICOM receipt
                  and 52 days from study to report, and it reads "Old scan". */}
              {/* Derived from the two dates, shown, and overridable — because only
                  the operator knows whether an oddly-dated series is genuinely
                  stale. Three states, not a boolean: "derive" is not "no". */}
              <div className="pt-2">
                <span className="text-xs font-medium text-gray-600 block mb-1">
                  Old scan
                  {studyAgeDays(ctInfo, meta.dateStr) != null && (
                    <span className="text-gray-400 font-normal">
                      {' '}— {studyAgeDays(ctInfo, meta.dateStr)} day
                      {studyAgeDays(ctInfo, meta.dateStr) === 1 ? '' : 's'} old at the report date
                    </span>
                  )}
                </span>
                <div className="flex gap-3 text-xs">
                  {[[undefined, `Derive (${isOldScan(ctInfo, undefined, meta.dateStr) ? 'old' : 'current'})`],
                    [true, 'Old'], [false, 'Current']].map(([v, lbl]) => (
                      <label key={String(v)} className="flex items-center gap-1">
                        <input type="radio" checked={oldScanOverride === v}
                          onChange={() => setOldScanOverride(v)} />
                        <span className="text-gray-600">{lbl}</span>
                      </label>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  When old, the study date prints in red and “- Old scan” is added to the
                  page 1 heading and the page 2 caption.
                </p>
              </div>

              {/* The measured volumes, shown where they were measured. Seeing the
                  figure here — before any PDF exists — is what makes the
                  transcription step genuinely gone rather than moved. */}
              <div className="pt-2 border-t border-gray-100 mt-2">
                <span className="text-xs font-semibold text-gray-600 block mb-1">Orbital volumes</span>
                {volumeState === 'measuring' && (
                  <p className="text-xs text-gray-500">Measuring orbital volumes…</p>
                )}
                {volumeState === 'idle' && (
                  <p className="text-xs text-gray-500">
                    No orbital volume meshes detected in this folder.
                  </p>
                )}
                {Object.entries(volumes).map(([key, v]) => (
                  <p key={key} className="text-xs text-gray-700">
                    {v.filename} — <strong>{v.cc.toFixed(2)}cc</strong>
                    {v.watertight === false && (
                      <span className="text-amber-700"> · not a closed surface</span>
                    )}
                  </p>
                ))}
                {/* Contextual help, shown only when it applies — a permanent
                    explanation of a condition most cases do not have is noise. */}
                {Object.values(volumes).some((v) => v.watertight === false) && (
                  <p className="text-[11px] text-amber-700 mt-1">
                    A volume is only exact on a sealed solid. The figure is still reported and
                    still prints — confirm it before sending. Nothing is blocked.
                  </p>
                )}
                <p className="text-[11px] text-gray-500 mt-1">
                  Measured from the meshes, not typed. These are the figures the colour key on
                  page 3 prints.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* No Page-1 header here: PAGE1_TITLES names the header of the STL/OBJ
            report's first page. A case report's page titles come from its template. */}
        <ReportLookPanel value={reportLook} showPage1Title={false}
          showToneControls={false} showBackground={false} />

        <button type="button" onClick={build}
          // `speHashing` too — review finding: a Build clicked mid-verification drafted with
          // spe:null and silently produced a PDF missing the plan-composed pages.
          // `volumeState === 'measuring'` for the same reason: an orbital report built
          // before its volumes land has no legend and no numbers on page 3.
          // NOT gated on the library any more. With the case folder gone, meshes
          // arrive only through a page's own file picker, and text pages take no
          // files: a report of Agreement + Terms drafted, showed "Draft — 3
          // pages", and left Build greyed with nothing saying why. What has to
          // be true is that the draft produced pages.
          // 🔴 NEVER gated on the report being complete. A report short of its
          // template is the team's NORMAL, DELIBERATE workflow: the pages this
          // tool cannot build — hand-drawn measurements, ZBrush colour maps —
          // are made in GIMP and merged in afterwards with PDF24. A build that
          // refused until every page was filled stopped the team working, to
          // cover a gap that is OURS. Deepak, 2026-09-23: "I do not want the
          // team to be prevented from generating report with missing pages …
          // BECAUSE you are not able to figure this out for weeks. So do not
          // block the team."
          //
          // The defect was never that the document is short. It is that nothing
          // SAID so — see the line under this button. State it, never gate it.
          // A hard block is for output that would be WRONG, not unfinished.
          disabled={!draft || !!draft?.error || !!busy || !draft?.doc?.pages?.length || speHashing
            || volumeState === 'measuring'}
          className={`${btn} w-full bg-brand-600 text-white`}>
          {speHashing ? 'Verifying meshes against the plan…'
            : volumeState === 'measuring' ? 'Measuring orbital volumes…'
              : busy === 'render' ? 'Rendering…' : busy === 'pdf' ? 'Building PDF…' : 'Build report'}
        </button>
        {/* What will NOT be in the PDF. Informational, never a refusal: the
            operator is told so nothing is silently short, and builds anyway. */}
        {unbuilt.length > 0 && !busy && (
          <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-2 -mt-2"
            data-testid="pages-not-in-pdf">
            <b>{unbuilt.length} of {pageBasket.basket?.entries.length ?? 0} pages</b> will not be
            in this PDF — {unbuilt.length === 1 ? 'it has' : 'they have'} no files yet:{' '}
            {unbuilt.join(', ')}. The report builds without {unbuilt.length === 1 ? 'it' : 'them'}.
          </p>
        )}
        {/* A blank sheet carries a FORMAT's chrome, so it needs one chosen —
            it used to throw "no geometry" when clicked before that. */}
        <button type="button" onClick={buildBlank} disabled={!!busy || !formatReady}
          className={`${btn} w-full border border-gray-300 bg-white text-gray-700`}
          title={formatReady ? 'One empty branded sheet to compose a hand-built page onto'
            : 'Choose the report format first'}>
          Blank page
        </button>
        {/* Team, 15 Oct 2026: "export the watermark page as a blank page with a transparent
            background. This allows the page to be used as a transparent overlay". The PDF above
            is unchanged — this rasterises it and lifts the background off. */}
        <button type="button" onClick={buildBlankOverlay} disabled={!!busy || !formatReady}
          data-testid="blank-page-transparent"
          className={`${btn} w-full border border-gray-300 bg-white text-gray-700`}
          title={formatReady ? 'The same sheet as a transparent PNG, to lay over your own images'
            : 'Choose the report format first'}>
          Blank page (transparent PNG)
        </button>
        {!!volumeErrors.length && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 -mt-2">
            {volumeErrors.map((e) => <p key={e}>{e}</p>)}
          </div>
        )}
        {progress && (
          <p className="text-xs text-gray-500 -mt-2">
            {progress.label} — {progress.done}/{progress.total}
          </p>
        )}
        {error && <p className="text-sm text-red-600" data-testid="builder-error">{error}</p>}
      </div>

      {/* ── right: what was detected, what is missing, the result ─────────── */}
      <div className="space-y-4">
        {!library.length && (
          <div className={`${card} text-sm text-gray-500`}>
            Add a page and give it its files to begin. Every mesh is classified by
            filename; you will see exactly what was detected before anything is drafted.
          </div>
        )}

        {draft?.error && (
          <div className={`${card} border-red-200 bg-red-50 text-sm text-red-700`}>{draft.error}</div>
        )}

        {!!library.length && (
          <div className={card}>
            <span className="text-sm font-semibold text-gray-700 block mb-2">
              Detected — {library.length} mesh{library.length === 1 ? '' : 'es'}
            </span>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {library.map((m) => (
                <div key={m.id} className="flex items-center gap-2 text-xs">
                  {/* Editable, because the filename is often silent. An override is
                      marked so the operator can see at a glance which rows are the
                      classifier's reading and which are their own. */}
                  {/* The clear option is an explicit entry, not "reselect what is
                      already selected": the select is controlled to `m.role`, so React
                      never fires change for an equal value and that branch could never
                      run. There has to be a value the operator can actually pick. */}
                  <select
                    value={m.role}
                    onChange={(e) => setRole(m.id, e.target.value === '__detected__' ? '' : e.target.value)}
                    title={roleOverrides[m.id] ? 'Set by you — choose "Detected" to hand it back to the classifier' : 'Detected from the filename'}
                    className={`w-40 shrink-0 rounded border px-1 py-0.5 ${roleOverrides[m.id]
                      ? 'border-brand-500 text-brand-700 font-medium' : 'border-transparent text-gray-700'}`}
                  >
                    {roleOverrides[m.id] && (
                      <option value="__detected__">↺ Detected</option>
                    )}
                    {Object.entries(ROLE_LABELS).map(([role, label]) => (
                      <option key={role} value={role}>{label}</option>
                    ))}
                  </select>
                  <span className="w-28 shrink-0 text-gray-500">{STAGE_LABELS[m.stage] ?? m.stage}</span>
                  <span className="text-gray-400 truncate">{m.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* The engine cannot tell a guide from a skull — both are 100% white in
            the team's exports. This is where the operator says. */}
        {!!library.length && (
          <PartTypes library={library} parts={parts} setParts={setParts} />
        )}

        {/* The engine will not choose. This is where the operator does. */}
        {!!conflicts.length && (
          <div className={`${card} ${unresolved.length ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
            <span className={`text-sm font-semibold block mb-2 ${unresolved.length ? 'text-amber-900' : 'text-gray-700'}`}>
              {unresolved.length
                ? `Choose which mesh to use (${unresolved.length} left)`
                : `Mesh choices (${conflicts.length}) — all resolved`}
            </span>
            {conflicts.map((c) => (
              <div key={c.key} className="mb-3">
                <span className={`text-xs font-medium block mb-1 ${c.chosen ? 'text-gray-600' : 'text-amber-900'}`}>
                  {c.label}
                  {!c.chosen && <span className="ml-1 font-normal">— {c.candidates.length} candidates</span>}
                </span>
                {c.candidates.map((cand) => (
                  <label key={cand.id}
                    className={`flex items-center gap-2 text-xs mb-0.5 ${c.chosen === cand.id ? 'text-gray-900 font-medium' : c.chosen ? 'text-gray-400' : 'text-amber-900'}`}>
                    <input type="radio" name={c.key} checked={picks[c.key] === cand.id}
                      onChange={() => setPicks((p) => ({ ...p, [c.key]: cand.id }))} />
                    <span className="truncate">{cand.name}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* The pages of this report, built up one at a time (ADR 0015). Not for a
            folder named by page, which states its own order. */}
        {!draft?.error && (
          <PageList
            onAddFiles={addFilesToPage}
            onAddImage={addImageToPage}
            liveAssets={pageAssets}
            basket={pageBasket.basket}
            offered={offered}
            draft={draft}
            library={library}
            availability={availability}
            apply={pageBasket.apply}
            startFromUsual={pageBasket.startFromUsual}
            clear={() => { pageBasket.clear(); startAnotherCase() }}
            edits={edits}
            setEdits={setEdits}
            onPreview={(id) => onePage(id, 'preview')}
            onExport={(id) => onePage(id, 'export')}
            busy={busy}
          />
        )}

        {/* One page, previewed on its own. Withdrawn with the built report when
            anything it was made from changes, so it never shows a stale page. */}
        {pagePreview && (
          <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="page-preview">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">
                Page preview — {previewPageTitle()}
              </span>
              <button type="button" className="text-xs text-gray-600 underline"
                onClick={() => setPagePreview((prev) => { if (prev?.url) URL.revokeObjectURL(prev.url); return null })}>
                Close
              </button>
            </div>
            <iframe title="Page preview" src={pagePreview.url}
              className="w-full h-[70vh] rounded-lg border border-gray-200" />
          </div>
        )}


        {draft?.doc && edited && (
          <div className={card}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">
                Draft — {edited.doc.pages.length} page{edited.doc.pages.length === 1 ? '' : 's'},
                {' '}{edited.doc.scenes.length} scene{edited.doc.scenes.length === 1 ? '' : 's'}
              </span>
              {!!Object.keys(edits).length && (
                <button
                  type="button"
                  onClick={clearCaseEdits}
                  className={`${btn} border border-gray-300 text-gray-700`}
                >
                  Clear {Object.keys(edits).length} edit{Object.keys(edits).length === 1 ? '' : 's'}
                </button>
              )}
            </div>

            {/* Whether the work is actually being kept. Silence here would mean
                an operator only discovers on refresh that nothing was saved. */}
            <p className="text-xs mb-2" data-testid="save-state">
              {!caseKey && (
                <span className="text-amber-700">
                  Not saved — enter a Case ID under <strong>Report details</strong> below and
                  your captions and annotations will be kept on this machine through a
                  refresh. (That field only names the report; it downloads nothing.)
                </span>
              )}
              {caseKey && saveState?.ok && !!Object.keys(edits).length && (
                <span className="text-gray-500">
                  Saved on this machine for <code>{caseKey}</code>.
                </span>
              )}
              {caseKey && saveState && !saveState.ok && (
                <span className="text-red-600">
                  NOT saved ({saveState.reason}) — your edits exist only until this page
                  reloads.
                </span>
              )}
              {restored && (
                <span className="text-gray-500">
                  {' '}Restored {Object.keys(restored.edits).length} edited page
                  {Object.keys(restored.edits).length === 1 ? '' : 's'}
                  {restored.savedAt ? ` from ${new Date(restored.savedAt).toLocaleString()}` : ''}.
                  {restored.dropped ? ` ${restored.dropped} unreadable entr${restored.dropped === 1 ? 'y was' : 'ies were'} dropped.` : ''}
                </span>
              )}
            </p>

            {/* The UNEDITED draft, deliberately. applyEdits DROPS pages marked
                "omit", so passing the edited copy made a page vanish from the
                list the instant it was omitted — with no way to bring it back
                short of clearing every edit. The editor needs the full set of
                pages; `edited.doc` is what gets built. */}
            <PageEditor
              doc={draft.doc}
              edits={edits}
              setEdits={setEdits}
              orphans={edited.orphans}
              allowOmit={false}
            />

            <p className="text-xs text-gray-400 mt-3">
              Long text pages may flow onto continuation pages, so the finished PDF can
              have more pages than this list. Edits are held for this session only —
              rebuild the report to see them.
            </p>
          </div>
        )}

        {built && (
          <div className={card}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">
                Report — {built.pages} page{built.pages === 1 ? '' : 's'}
              </span>
              <div className="flex items-center gap-2">
                {/* Team, 15 Oct 2026: PNGs of the output. Two kinds, because the team asked for
                    "individual images" and the renders and the finished pages are both that
                    (Deepak, 17 Oct: both). Verbs on buttons, nouns in the labels. */}
                <button type="button" onClick={exportRenders} disabled={!!busy}
                  data-testid="export-renders-png"
                  title="Each model view as its own PNG — no frame, header or watermark"
                  className={`${btn} border border-gray-300 bg-white text-gray-700`}>
                  Export renders (PNG)
                </button>
                <button type="button" onClick={exportPagePngs} disabled={!!busy}
                  data-testid="export-pages-png"
                  title="Each finished page as a PNG, rasterised from this PDF"
                  className={`${btn} border border-gray-300 bg-white text-gray-700`}>
                  Export pages (PNG)
                </button>
                <button type="button" onClick={download} className={`${btn} bg-brand-600 text-white`}>
                  Download PDF
                </button>
              </div>
            </div>
            {!!built.warnings.length && built.warnings.map((w) => (
              <p key={w} className="text-xs text-amber-700 mb-1">{w}</p>
            ))}
            <iframe title="Case report preview" src={pdfUrl}
              className="w-full h-[70vh] rounded-lg border border-gray-200" />
          </div>
        )}
      </div>
    </div>
  )
}
